/**
 * Product setup revamp — who gets which product screen (merchant routing, 2026-10-07).
 *
 * `/products` is ONE sidebar row ("All Products") for every role, and `GET products` answers
 * per role (`AdminProductController` index: a merchant gets only its own `company_product`
 * rows). In v1 the same `/products` listing shows merchants their own columns and edits them
 * through `/products/update-company-fields/:id`. The v2 overview and editor are the STAFF
 * screens, so:
 *
 * - **Merchant roles** (`admin`, `MerchantGroupAdmin` — `isMerchantRole`) are sent to the legacy
 *   v1 screen (`merchantProductsHref`) until the v2 merchant screen exists (PLAN P2.25-P2.27).
 *   The route guard covers typed URLs, bookmarks, the e-commerce sync return
 *   (`/products?sync=…`) and impersonation.
 * - **Product development** (`canReadPd`): the PD reads and the links to PD screens need
 *   `view-product-development` (no super-admin bypass, like the backend's route check).
 * - **Roles without `edit-product`** that still reach the screen get a read-only overview: no
 *   Create / Edit / Duplicate / Delete, no way into the editor. The backend would refuse them
 *   anyway (`ProductPolicy::view` / `update`: super roles and factories only).
 *
 * PRE-PROD CLICKABLE BUILD: the routes are registered only with VITE_PRODUCT_SETUP_V2=true and
 * carry `meta.permission: 'product-setup-v2'` (router/index.ts); this guard runs after that.
 * The decision fails closed: without a permission list (no session loaded) a build is
 * read-only, never staff. Only DEV (the offline demo fixtures) treats "no list" as staff.
 */
import { computed, type ComputedRef } from 'vue';
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router';
import { isMerchantRole } from '@/composables/useMenuHref';
import { useMainStore } from '@/store';

export interface ProductAccess {
  /** The role the decision was made for (null: no session). */
  role: string | null;
  /** A merchant role: the staff screens are not for it (legacy v1 until P2.25-P2.27). */
  merchant: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canCopy: boolean;
  canDelete: boolean;
  /** The legacy merchant layer of a product (`edit-company-product`; v1 `/products/update-company-fields`). */
  canMerchantFields: boolean;
  /**
   * Product development reads and screens (`view-product-development`). The backend checks the
   * route permission on the user's role with NO super-admin bypass, so neither does this:
   * without it the PD panels say so and no `product-development/*` request is made.
   */
  canReadPd: boolean;
}

/**
 * The decision. `permissions` null / empty = no session: read-only (fail closed), except in DEV
 * (`dev`, default `import.meta.env.DEV`), where the offline demo data acts as staff.
 */
export const productAccessFor = (input: {
  roleName?: string | null;
  permissions?: readonly string[] | null;
  dev?: boolean;
}): ProductAccess => {
  const permissions = input.permissions?.length ? input.permissions : null;
  const role = input.roleName ?? null;
  const merchant = isMerchantRole(role);
  if (!permissions) {
    const staff = (input.dev ?? import.meta.env.DEV) && !merchant;
    return {
      role,
      merchant,
      canCreate: staff,
      canEdit: staff,
      canCopy: staff,
      canDelete: staff,
      canMerchantFields: staff,
      canReadPd: staff,
    };
  }
  const has = (name: string) => permissions.includes(name);
  return {
    role,
    merchant,
    canCreate: !merchant && has('create-product'),
    canEdit: !merchant && has('edit-product'),
    canCopy: !merchant && has('copy-product'),
    canDelete: !merchant && has('delete-product'),
    canMerchantFields: has('edit-company-product'),
    canReadPd: has('view-product-development'),
  };
};

const LEGACY_BASE = String(import.meta.env.VITE_VUE2_APP_URL ?? '').replace(/\/+$/, '');

/**
 * Where a merchant goes instead of a staff product screen: the legacy listing (query kept —
 * the e-commerce sync returns to `/products?sync=…&post_id=…`), or the legacy MERCHANT edit
 * page for a product (never the staff editor).
 */
export const merchantProductsHref = (
  to: Pick<RouteLocationNormalized, 'path' | 'params' | 'query'>,
  base: string = LEGACY_BASE
): string => {
  const id = Array.isArray(to.params.id) ? to.params.id[0] : to.params.id;
  const path = id ? `/products/update-company-fields/${id}` : '/products';
  const query = new URLSearchParams();
  if (!id) {
    for (const [key, value] of Object.entries(to.query)) {
      for (const v of Array.isArray(value) ? value : [value])
        if (typeof v === 'string') query.append(key, v);
    }
  }
  const search = query.toString();
  return `${base}${path}${search ? `?${search}` : ''}`;
};

const accessFromStore = (): ProductAccess => {
  const store = useMainStore();
  return productAccessFor({
    roleName: store.role?.name ?? null,
    permissions: store.permissions ?? null,
  });
};

/** The access of the signed-in user, reactive (the store's role and permission list). */
export const useProductAccess = (): ComputedRef<ProductAccess> => computed(() => accessFromStore());

/**
 * `beforeEnter` of the product routes (runs after the global guard has loaded the user and
 * checked `meta.permission`). A merchant leaves for the legacy screen. A role that cannot edit
 * is turned back from the editor to the read-only overview.
 */
export const productRoutesGuard = (to: RouteLocationNormalized): boolean | RouteLocationRaw => {
  const access = accessFromStore();
  if (access.merchant) {
    window.location.replace(merchantProductsHref(to));
    return false;
  }
  const editor = to.path !== '/products' && to.path !== '/products/';
  const allowed = to.path.startsWith('/products/add') ? access.canCreate : access.canEdit;
  if (editor && !allowed) {
    const query = { ...to.query, readOnly: '1' };
    return { path: '/products', query };
  }
  return true;
};
